<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('deliveryOrder.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('deliveryOrder.pageTitle'),
            href: '/admin/delivery-orders',
          },
          {
            text: $t('create'),
            active: true,
          },
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-md-3">
              <div class="ts-border ts-border-gray-200 ts-rounded ts-p-3">
                <div class="row ts-space-y-2">
                  <div class="col-md-12">
                    <label class="required">{{
                      $t('deliveryOrder.doDate')
                    }}</label>
                    <a-date-picker
                      v-model="model.do_date"
                      format="DD-MM-YYYY"
                      valueFormat="DD-MM-YYYY"
                      class="ts-w-full"
                    ></a-date-picker>
                    <div class="validate" v-if="errors.has('do_date')">
                      {{ errors.first('do_date') }}
                    </div>
                  </div>
                  <div class="col-md-12">
                    <label class="required">{{
                      $t('deliveryOrder.seller')
                    }}</label>
                    <a-select
                      class="ts-w-full"
                      v-model="model.seller_id"
                      @change="onSellerChange"
                      showSearch
                      :options="employeeList"
                      :filterOption="
                        (input, option) =>
                          option.componentOptions.children[0].text
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                      "
                    ></a-select>
                    <div class="validate" v-if="errors.has('seller_id')">
                      {{ errors.first('seller_id') }}
                    </div>
                  </div>
                  <div class="col-md-12">
                    <label>{{$t('deliveryOrder.invoice')}}</label>
                    <div class="ts-border ts-border-blue-600 ts-p-3 ts-rounded">
                      <Search v-model="searchText" />
                      <div class="ts-clear-both"></div>
                      <div style="height: 25vh;" class="ts-overflow-scroll ts-mt-3">
                        <table class="ts-w-full">
                          <thead>
                            <tr>
                              <td class="ts-uppercase ts-font-semibold ts-bg-blue-600 ts-text-white ts-p-1 ts-border-r-2 ts-border-white ts-w-4">{{ $t('deliveryOrder.no') }}</td>
                              <td class="ts-uppercase ts-font-semibold ts-bg-blue-600 ts-text-white ts-p-1 ts-border-r-2 ts-border-white">{{ $t('deliveryOrder.number') }}</td>
                              <td class="ts-uppercase ts-font-semibold ts-bg-blue-600 ts-text-white ts-p-1 ts-w-24">{{ $t('deliveryOrder.date') }}</td>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(inv, index) in invoices"
                              :key="index"
                              class="ts-border-b ts-border-gray-200 ts-cursor-pointer hover:ts-bg-blue-200"
                              @click.prevent="onClickInvoiceNunmber(inv.stock_out_id)"
                            >
                              <td class="ts-p-1 ts-text-center">{{ index + 1 }}</td>
                              <td class="ts-p-1">{{ inv.invoice_no }}</td>
                              <td class="ts-p-1">{{ inv.stock_out_date }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <small><i>{{ $t('deliveryOrder.clickToAdd') }}</i></small>
                    </div>
                  </div>
                  <div class="col-md-12">
                    <label>{{$t('deliveryOrder.remark')}}</label>
                    <textarea v-model="model.remarks" rows="3" class="form-control"></textarea>
                    <div class="validate" v-if="errors.has('remarks')">
                      {{ errors.first('remarks') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-9">
              <h5 class="ts-bg-blue-500 ts-uppercase ts-text-white ts-p-1 ts-rounded-sm">{{ $t('deliveryOrder.invoiceDetail') }}</h5>
              <div class="ts-pl-8">
                <DetailItem v-model="model" :validate="errors" />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12 ts-flex ts-justify-end ts-space-x-3">
              <ts-button
                @click.prevent="$router.push({ name: 'delivery-order' })"
                >{{ $t('cancel') }}</ts-button
              >
              <ts-button
                color="primary"
                :waiting="waiting"
                @click.prevent="onSave"
                >{{ $t('savePrint') }}</ts-button
              >
            </div>
          </div>
        </ts-loading-banner>
        <a-modal
          :title="$t('pdf')"
          width="50%"
          v-model="show_pdf"
          :footer="null"
        >
          <ts-preview-pdf v-model="waiting" :src="src" />
        </a-modal>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import Search from './components/search-box'
import DetailItem from './components/invoice-detail.vue'
import Draggable from 'vuedraggable'

export default {
  name: 'deliveryOrder',
  components: {
    Search,
    DetailItem,
    Draggable,
  },
  data() {
    return {
      loading: false,
      waiting: false,
      show_pdf: false,
      src: '',
      errors: new Errors(),
      searchText: {
        text: '',
      },
      model: {
        do_date: moment().format('DD-MM-YYYY'),
        remarks: null,
        seller_id: null,
        stock_out_id_list: [],
      },
    }
  },
  created() {
    this.fetchResource()
  },
  computed: {
    employeeList() {
      return this.$store.state.wholesale.deliveryOrder.employees.map((el) => ({
        value: el.employee_id,
        label: el.employee_name_en,
      }))
    },
    invoices(){
      return this.$store.state.wholesale.deliveryOrder.invoices
        .filter(
          (i) =>
            !_.includes(
              this.model.stock_out_id_list.map((el) => el.stock_out_id),
              i.stock_out_id
            )
        )
        .filter((p) =>
          this.searchText.text
            .toLowerCase()
            .split(' ')
            .every((s) => p.invoice_no.toLowerCase().includes(s))
        )
    }
  },
  methods: {
    ...mapActions('wholesale/deliveryOrder', [
      'fetchData',
      'store',
      'getInvoice',
      'getInvoiceDetail'
    ]),
    async fetchResource() {
      this.loading = true
      await this.fetchData()
      this.loading = false
    },
    onSave() {
      this.waiting = true
      this.show_pdf = true
      this.errors = new Errors()
      this.store(this.model)
        .then((response) => {
          this.$toasted.success(response.message)
          this.clearInput()
          this.src = response.url
        })
        .catch((error) => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
          this.show_pdf = false
        })
        .finally(() => {
          this.waiting = false
        })
    },
    async onSellerChange() {
      this.loading = true
      await this.getInvoice(this.model.seller_id)
      this.loading = false
    },
    clearInput() {
      this.model.do_date = moment().format('DD-MM-YYYY')
      this.model.remarks = null
      this.model.stock_out_id_list = []
      this.$store.commit('wholesale/deliveryOrder/SET_INVOICE_LIST', [])
    },
    onClickInvoiceNunmber(stockoutId){
      this.loading = true
      this.getInvoiceDetail(stockoutId)
        .then(response => {
          this.model.stock_out_id_list.push({
            stock_out_id: response.data.stock_out_id,
            invoice_no: response.data.invoice_no,
            details: response.data.details.map(item => ({
                product_id: item.product_id,
                product_name_en: item.product_name_en,
                barcode: item.barcode,
                uom_id: item.uom_id,
                uom_name_en: item.uom_name_en,
                quantity: item.quantity,
                qty_delivered: item.quantity,
                stock_out_type_id: item.stock_out_type_id,
            }))
          })
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.box-hieght {
  min-height: 375px !important;
  max-height: 380px;
  overflow-y: scroll;
}
.draggable-list {
  min-height: 100px;
}
/* Unfortunately @apply cannot be setup in codesandbox, 
	but you'd use "@apply border opacity-50 border-blue-500 bg-gray-200" here */
.moving-card {
  opacity: 0.5;
  background: #f7fafc;
  border: 1px solid #4299e1;
}
</style>